import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Categories.css';
import API_BASE_URL from '../../api';

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState(null);
  const [sortOrder, setSortOrder] = useState('');
  const [isEnabled, setIsEnabled] = useState(true); // add-modal enabled

  const [editCategory, setEditCategory] = useState(null);
  const [newName, setNewName] = useState('');
  const [editSortOrder, setEditSortOrder] = useState('');
  const [editEnabled, setEditEnabled] = useState(true); // edit-modal enabled
  const [stockCode, setStockCode] = useState('');
  const [editStockCode, setEditStockCode] = useState('');

  const [error, setError] = useState('');
  const [image, setImage] = useState(null);

  const [openCategories, setOpenCategories] = useState({});
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editFocusField, setEditFocusField] = useState(null); // 'name' | 'sort' | null

  // inline popover editor: { categoryId, field: 'name' | 'sort_order', value }
  const [inlineEdit, setInlineEdit] = useState(null);

  // multi-select + filters
  const [selectedIds, setSelectedIds] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [rootFilterId, setRootFilterId] = useState(''); // dropdown of main cats

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    const next = getNextSortOrderForParent(parentId);
    setSortOrder(next.toString());
  }, [categories, parentId]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/categories`);
      setCategories(response.data);
    } catch (err) {
      console.error('Failed to fetch categories', err);
      setError('Failed to fetch categories');
    }
  };

  const getNextSortOrderForParent = (parent) => {
    const siblings = categories.filter((c) => c.parent_id === parent);
    if (siblings.length === 0) return 1;
    const maxOrder = Math.max(
        ...siblings.map((c) => (c.sort_order == null ? 0 : c.sort_order))
    );
    return maxOrder + 1;
  };

  const resetAddForm = () => {
    setName('');
    setParentId(null);
    setSelectedCategories([]);
    setImage(null);
    setError('');
    setIsEnabled(true);
    const next = getNextSortOrderForParent(null);
    setSortOrder(next.toString());
  };

  const handleAddCategory = async () => {
    if (!name) {
      setError('Category name is required');
      return;
    }

    const formData = new FormData();
    formData.append('name', name);
    formData.append('parentId', parentId ? parentId : '');
    formData.append('sort_order', sortOrder || '');
    formData.append('enabled', isEnabled ? '1' : '0');
    formData.append('stock_code', stockCode.toUpperCase().slice(0, 10));
    if (image) formData.append('image', image);

    try {
      await axios.post(`${API_BASE_URL}/api/categories`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await fetchCategories();
      setStockCode('');
      resetAddForm();
      setIsAddModalOpen(false);
    } catch (err) {
      console.error('Failed to add category', err);
      const msg = err?.response?.data?.error || 'Failed to add category';
      setError(msg);
    }
  };

  const handleEditCategory = async () => {
    if (!newName) {
      setError('Category name is required');
      return;
    }

    const formData = new FormData();
    formData.append('name', newName);
    formData.append('parentId', parentId ? parentId : '');
    formData.append('sort_order', editSortOrder || '');
    formData.append('enabled', editEnabled ? '1' : '0');
    formData.append('stock_code', editStockCode.toUpperCase().slice(0, 10));
    if (image) formData.append('image', image);

    try {
      await axios.put(
          `${API_BASE_URL}/api/categories/${editCategory.id}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      await fetchCategories();
      setEditCategory(null);
      setNewName('');
      setEditSortOrder('');
      setSelectedCategories([]);
      setImage(null);
      setError('');
      setIsEditModalOpen(false);
      setEditFocusField(null);
    } catch (err) {
      console.error('Failed to edit category', err);
      const msg = err?.response?.data?.error || 'Failed to edit category';
      setError(msg);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;

    try {
      await axios.delete(`${API_BASE_URL}/api/categories/${categoryId}`);
      await fetchCategories();
      setSelectedIds((prev) => prev.filter((id) => id !== categoryId));
      setError('');
    } catch (err) {
      console.error('Failed to delete category', err);
      const msg = err?.response?.data || 'Failed to delete category';
      setError(msg);
    }
  };

  const handleSelectCategory = (level, categoryId) => {
    const newSelectedCategories = selectedCategories.slice(0, level);
    newSelectedCategories[level] = categoryId;
    setSelectedCategories(newSelectedCategories);
    setParentId(categoryId);
    setError('');
  };

  // open the Edit modal
  const handleEditButtonClick = (category, focusField = null) => {
    setEditCategory(category);
    setNewName(category.name);
    setEditSortOrder(
        category.sort_order != null ? category.sort_order.toString() : '1'
    );
    setParentId(category.parent_id);
    setEditEnabled(!!category.enabled);
    setEditStockCode(category.stock_code || '');
    setImage(null);
    setError('');
    setEditFocusField(focusField);
    setIsEditModalOpen(true);
  };

  // ---------- inline popover editing ----------

  const startInlineEdit = (category, field) => {
    setInlineEdit({
      categoryId: category.id,
      field,
      value:
          field === 'name'
              ? category.name
              : category.sort_order != null
                  ? category.sort_order.toString()
                  : '',
    });
  };

  const cancelInlineEdit = () => {
    setInlineEdit(null);
  };

  const handleInlineSave = async () => {
    if (!inlineEdit) return;

    const category = categories.find((c) => c.id === inlineEdit.categoryId);
    if (!category) {
      setInlineEdit(null);
      return;
    }

    const formData = new FormData();
    formData.append(
        'name',
        inlineEdit.field === 'name' ? inlineEdit.value : category.name
    );
    formData.append('parentId', category.parent_id ?? '');
    formData.append(
        'sort_order',
        inlineEdit.field === 'sort_order'
            ? inlineEdit.value || ''
            : category.sort_order != null
                ? category.sort_order.toString()
                : ''
    );
    formData.append('enabled', category.enabled ? '1' : '0');

    try {
      await axios.put(
          `${API_BASE_URL}/api/categories/${category.id}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      await fetchCategories();
      setInlineEdit(null);
      setError('');
    } catch (err) {
      console.error('Failed to edit category', err);
      const msg = err?.response?.data?.error || 'Failed to edit category';
      setError(msg);
    }
  };

  // ---------- hierarchy / table helpers ----------

  const getSubcategories = (parentId) =>
      categories.filter((category) => category.parent_id === parentId);

  const renderDropdowns = () => {
    const dropdowns = [];
    let currentParentId = null;

    for (let level = 0; level <= selectedCategories.length; level++) {
      const subcategories = getSubcategories(currentParentId);
      if (subcategories.length === 0) break;

      dropdowns.push(
          <div key={level} className="input-container">
            <select
                value={selectedCategories[level] || ''}
                onChange={(e) =>
                    handleSelectCategory(
                        level,
                        e.target.value ? parseInt(e.target.value, 10) : null
                    )
                }
            >
              <option value="">Select a category</option>
              {subcategories
                  .slice()
                  .sort((a, b) => {
                    const ao = a.sort_order ?? 0;
                    const bo = b.sort_order ?? 0;
                    if (ao === bo) return a.id - b.id;
                    return ao - bo;
                  })
                  .map((category) => (
                      <option
                          key={category.id}
                          value={category.id}
                          style={{ paddingLeft: level * 20 }}
                      >
                        {level > 0 ? '— ' : ''}
                        {category.name}
                      </option>
                  ))}
            </select>
          </div>
      );

      currentParentId = selectedCategories[level];
    }

    return dropdowns;
  };

  const getCategoryChain = (categoryId) => {
    const chain = [];
    let currentCategoryId = categoryId;

    while (currentCategoryId) {
      const category = categories.find((cat) => cat.id === currentCategoryId);
      if (category) {
        chain.unshift(category.name);
        currentCategoryId = category.parent_id;
      } else {
        currentCategoryId = null;
      }
    }

    return chain.join(' > ');
  };

  const getSelectedCategoryChain = () =>
      parentId ? getCategoryChain(parentId) : 'No parent category selected';

  const toggleCategoryOpen = (id) => {
    setOpenCategories((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // date formatter (for created_at / updated_at)
  const formatDate = (value) => {
    if (!value) return '—';
    try {
      return new Date(value).toLocaleString();
    } catch {
      return value;
    }
  };

  // build visible list (respects expand/collapse + root filter)
  const getVisibleTableCategories = () => {
    const byParent = new Map();
    categories.forEach((cat) => {
      const pid = cat.parent_id ?? null;
      if (!byParent.has(pid)) byParent.set(pid, []);
      byParent.get(pid).push(cat);
    });

    for (const list of byParent.values()) {
      list.sort((a, b) => {
        const ao = a.sort_order ?? 0;
        const bo = b.sort_order ?? 0;
        if (ao === bo) return a.id - b.id;
        return ao - bo;
      });
    }

    const result = [];

    const walk = (cat, level, ancestorsOpen) => {
      if (!ancestorsOpen) return;

      result.push({ category: cat, level });

      const children = byParent.get(cat.id) || [];
      const childrenVisible = ancestorsOpen && !!openCategories[cat.id];

      children.forEach((child) => walk(child, level + 1, childrenVisible));
    };

    const roots = byParent.get(null) || [];
    const rootIdInt = rootFilterId ? parseInt(rootFilterId, 10) : null;
    const rootsToWalk = rootIdInt ? roots.filter((r) => r.id === rootIdInt) : roots;

    rootsToWalk.forEach((root) => walk(root, 0, true));

    return result;
  };

  const hasChildren = (categoryId) =>
      categories.some((c) => c.parent_id === categoryId);

  const getFilteredVisibleCategories = () => {
    const allVisible = getVisibleTableCategories();
    if (!filterText.trim()) return allVisible;
    const ft = filterText.toLowerCase();
    return allVisible.filter(({ category }) => {
      const name = (category.name || '').toLowerCase();
      const chain = getCategoryChain(category.id).toLowerCase();
      return name.includes(ft) || chain.includes(ft);
    });
  };

  // ---------- multi-select & bulk actions ----------

  const handleCheckboxChange = (id) => {
    setSelectedIds((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleToggleSelectAll = () => {
    const visible = getFilteredVisibleCategories();
    const visibleIds = visible.map((r) => r.category.id);
    if (visibleIds.length === 0) return;

    const allSelected = visibleIds.every((id) => selectedIds.includes(id));

    if (allSelected) {
      // unselect all visible
      setSelectedIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      // select all visible
      setSelectedIds((prev) =>
          Array.from(new Set([...prev, ...visibleIds]))
      );
    }
  };

  const updateManyEnabled = async (ids, enabledValue) => {
    if (ids.length === 0) {
      alert('No categories selected.');
      return;
    }

    try {
      await Promise.all(
          ids.map((id) => {
            const cat = categories.find((c) => c.id === id);
            if (!cat) return null;

            const formData = new FormData();
            formData.append('name', cat.name);
            formData.append('parentId', cat.parent_id ?? '');
            formData.append(
                'sort_order',
                cat.sort_order != null ? cat.sort_order.toString() : ''
            );
            formData.append('enabled', enabledValue ? '1' : '0');

            return axios.put(
                `${API_BASE_URL}/api/categories/${id}`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
          })
      );
      await fetchCategories();
      setError('');
    } catch (err) {
      console.error('Failed to update categories', err);
      const msg = err?.response?.data?.error || 'Failed to update categories';
      setError(msg);
    }
  };

  const handleBulkEnableDisable = async (enabledValue) => {
    await updateManyEnabled(selectedIds, enabledValue);
  };

  const handleBulkEnableDisableAll = async (enabledValue) => {
    const label = enabledValue ? 'enable ALL categories' : 'disable ALL categories';
    if (!window.confirm(`Are you sure you want to ${label}?`)) return;
    const allIds = categories.map((c) => c.id);
    await updateManyEnabled(allIds, enabledValue);
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      alert('No categories selected.');
      return;
    }

    if (
        !window.confirm(
            `Are you sure you want to delete ${selectedIds.length} categories? This cannot be undone.`
        )
    ) {
      return;
    }

    try {
      await Promise.all(
          selectedIds.map((id) =>
              axios.delete(`${API_BASE_URL}/api/categories/${id}`)
          )
      );
      await fetchCategories();
      setSelectedIds([]);
      setError('');
    } catch (err) {
      console.error('Failed to delete categories', err);
      const msg = err?.response?.data || 'Failed to delete categories';
      setError(msg);
    }
  };

  const handleToggleEnabledSingle = async (category) => {
    const newEnabled = !category.enabled;

    const formData = new FormData();
    formData.append('name', category.name);
    formData.append('parentId', category.parent_id ?? '');
    formData.append(
        'sort_order',
        category.sort_order != null ? category.sort_order.toString() : ''
    );
    formData.append('enabled', newEnabled ? '1' : '0');

    try {
      await axios.put(
          `${API_BASE_URL}/api/categories/${category.id}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      await fetchCategories();
      setError('');
    } catch (err) {
      console.error('Failed to toggle enabled', err);
      const msg = err?.response?.data?.error || 'Failed to toggle enabled';
      setError(msg);
    }
  };

  // ---------- derive for render ----------

  const filteredVisibleCategories = getFilteredVisibleCategories();
  const visibleIds = filteredVisibleCategories.map((r) => r.category.id);
  const allVisibleSelected =
      visibleIds.length > 0 &&
      visibleIds.every((id) => selectedIds.includes(id));

  const rootCategories = categories.filter((c) => c.parent_id == null);

  // ---------- render ----------

  return (
      <div>
        <h1>Category Manager</h1>

        {/* Toolbar */}
        <div className="toolbar">
          <div className="toolbar-left">
            <div className="toolbar-filter-group">
              <div className="toolbar-filter-item toolbar-cat-item">
                <select
                    className="toolbar-select"
                    value={rootFilterId}
                    onChange={(e) => setRootFilterId(e.target.value)}
                >
                  <option value="">All categories</option>
                  {rootCategories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div className="toolbar-filter-divider" />
              <div className="toolbar-filter-item">
                <i className="fas fa-search toolbar-filter-icon" />
                <input
                    type="text"
                    className="toolbar-search"
                    placeholder="Search categories..."
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="toolbar-right">
            <button className="toolbar-btn toolbar-insert" onClick={() => { resetAddForm(); setIsAddModalOpen(true); }}>
              <i className="fas fa-plus" /> Add
            </button>
            <div className="toolbar-btn-divider" />
            <button className="toolbar-btn toolbar-enable" onClick={() => handleBulkEnableDisable(true)}>
              <i className="fas fa-eye" /> Enable
            </button>
            <button className="toolbar-btn toolbar-disable" onClick={() => handleBulkEnableDisable(false)}>
              <i className="fas fa-eye-slash" /> Disable
            </button>
            <div className="toolbar-btn-divider" />
            <button className="toolbar-btn toolbar-delete" onClick={handleBulkDelete}>
              <i className="fas fa-trash" /> Delete
            </button>
          </div>
        </div>

        {error && <p style={{ color: 'red' }}>{error}</p>}

        {/* ADD MODAL */}
        {isAddModalOpen && (
            <div className="modal-overlay" onClick={() => setIsAddModalOpen(false)}>
              <div className="modal" onClick={(e) => e.stopPropagation()}>
                <h2>Add Category</h2>

                <div className="input-container">
                  <input
                      type="text"
                      placeholder="Category Name"
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value);
                        setError('');
                      }}
                  />
                </div>

                <div className="input-container">
                  <input
                      type="text"
                      maxLength={10}
                      placeholder="Stock Code (e.g. FRG)"
                      value={stockCode}
                      onChange={(e) => setStockCode(e.target.value.toUpperCase())}
                  />
                </div>

                <div className="input-container">
                  <input
                      type="number"
                      min="1"
                      placeholder="Sort order"
                      value={sortOrder}
                      onChange={(e) => setSortOrder(e.target.value)}
                  />
                </div>

                <div className="input-container checkbox-row">
                  <label>
                    <input
                        type="checkbox"
                        checked={isEnabled}
                        onChange={(e) => setIsEnabled(e.target.checked)}
                    />{' '}
                    Display on site
                  </label>
                </div>

                <div className="input-container">
                  <input
                      type="file"
                      onChange={(e) => setImage(e.target.files[0])}
                  />
                </div>

                {/* Parent dropdown chain */}
                {renderDropdowns()}

                <p style={{ fontSize: '0.9rem', opacity: 0.8 }}>
                  {parentId
                      ? `You are adding a subcategory to: ${getSelectedCategoryChain()}`
                      : 'You are adding a main category'}
                </p>

                <div className="modal-buttons">
                  <button className="button-style" onClick={handleAddCategory}>
                    Save
                  </button>
                  <button
                      className="button-style secondary"
                      onClick={() => {
                        setIsAddModalOpen(false);
                        resetAddForm();
                      }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
        )}

        {/* EDIT MODAL */}
        {isEditModalOpen && editCategory && (
            <div className="modal-overlay" onClick={() => setIsEditModalOpen(false)}>
              <div className="modal" onClick={(e) => e.stopPropagation()}>
                <h2>Edit Category (ID {editCategory.id})</h2>

                <div className="input-container">
                  <input
                      type="text"
                      placeholder="Category Name"
                      value={newName}
                      onChange={(e) => {
                        setNewName(e.target.value);
                        setError('');
                      }}
                      autoFocus={editFocusField === 'name' || editFocusField === null}
                  />
                </div>

                <label>Stock Code (e.g. FRG)</label>
                <input
                    type="text"
                    maxLength={10}
                    placeholder="e.g. FRG"
                    value={editStockCode}
                    onChange={(e) => setEditStockCode(e.target.value.toUpperCase())}
                />

                <div className="input-container">
                  <input
                      type="number"
                      min="1"
                      placeholder="Sort order"
                      value={editSortOrder}
                      onChange={(e) => setEditSortOrder(e.target.value)}
                      autoFocus={editFocusField === 'sort'}
                  />
                </div>

                <div className="input-container checkbox-row">
                  <label>
                    <input
                        type="checkbox"
                        checked={editEnabled}
                        onChange={(e) => setEditEnabled(e.target.checked)}
                    />{' '}
                    Display on site
                  </label>
                </div>

                <div className="input-container">
                  <input
                      type="file"
                      onChange={(e) => setImage(e.target.files[0])}
                  />
                </div>

                <p style={{ fontSize: '0.9rem', opacity: 0.8 }}>
                  Current path: {getCategoryChain(editCategory.id)}
                </p>

                <div className="modal-buttons">
                  <button className="button-style" onClick={handleEditCategory}>
                    Save
                  </button>
                  <button
                      className="button-style secondary"
                      onClick={() => {
                        setIsEditModalOpen(false);
                        setEditCategory(null);
                        setNewName('');
                        setEditSortOrder('');
                        setImage(null);
                        setEditFocusField(null);
                        setError('');
                      }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
        )}

        {/* TABLE VIEW with scrollable container */}
        <div className="categories-table-container">
          <table className="categories-table">
            <thead>
            <tr>
              <th className="arrow-col"></th>
              <th className="select-col">
                <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={handleToggleSelectAll}
                />
              </th>
              <th>ID</th>
              <th>Lock</th>
              <th>Img</th>
              <th>Category Name</th>
              <th>Sort Order</th>
              <th>Enabled</th>
              <th className="date-col">Date Added</th>
              <th className="date-col">Date Modified</th>
              <th style={{ width: '160px' }}>Actions</th>
            </tr>
            </thead>
            <tbody>
            {filteredVisibleCategories.map(({ category, level }) => {
              const open = !!openCategories[category.id];
              const children = hasChildren(category.id);
              const enabled = !!category.enabled;
              const isSelected = selectedIds.includes(category.id);

              const isNameEditing =
                  inlineEdit &&
                  inlineEdit.categoryId === category.id &&
                  inlineEdit.field === 'name';

              const isSortEditing =
                  inlineEdit &&
                  inlineEdit.categoryId === category.id &&
                  inlineEdit.field === 'sort_order';

              const childCount = categories.filter((c) => c.parent_id === category.id).length;

              return (
                  <React.Fragment key={category.id}>
                    {level === 0 && (
                        <tr className="cat-section-header-row">
                          <td colSpan={11} className="cat-section-header-cell">
                            <div className="cat-section-header-inner">
                              {category.image_url && (
                                  <img src={category.image_url} alt="" className="cat-section-header-img" />
                              )}
                              <span className="cat-section-header-name">{category.name}</span>
                              {childCount > 0 && (
                                  <span className="cat-section-header-count">{childCount} sub{childCount === 1 ? 'category' : 'categories'}</span>
                              )}
                            </div>
                          </td>
                        </tr>
                    )}
                    <tr
                        className={level === 0 ? 'main-category-row' : ''}
                    >
                      <td className="arrow-col">
                        {children ? (
                            <button
                                type="button"
                                className="category-toggle"
                                onClick={() => toggleCategoryOpen(category.id)}
                            >
                              {open ? '▾' : '▸'}
                            </button>
                        ) : null}
                      </td>

                      <td className="select-col">
                        <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleCheckboxChange(category.id)}
                        />
                      </td>

                      <td>{category.id}</td>

                      <td className="lock-col">
                        {category.is_protected ? '🔒' : ''}
                      </td>

                      <td className="categories-table-image-cell">
                        {category.image_url ? (
                            <img
                                src={category.image_url}
                                alt={category.name}
                                className="categories-table-image"
                            />
                        ) : (
                            <span className="no-image">—</span>
                        )}
                      </td>

                      {/* NAME CELL with inline editor + pencil */}
                      <td
                          className="clickable-cell"
                          onClick={(e) => {
                            e.stopPropagation();
                            startInlineEdit(category, 'name');
                          }}
                      >
                    <span style={{ paddingLeft: level * 16 }}>
                      {category.name}
                      <span
                          className="cat-edit-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            startInlineEdit(category, 'name');
                          }}
                      >
                        ✏️
                      </span>
                    </span>

                        {isNameEditing && (
                            <div
                                className="inline-editor"
                                onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                  type="text"
                                  value={inlineEdit.value}
                                  onChange={(e) =>
                                      setInlineEdit({
                                        ...inlineEdit,
                                        value: e.target.value,
                                      })
                                  }
                              />
                              <button
                                  type="button"
                                  className="inline-btn inline-btn-save"
                                  onClick={handleInlineSave}
                              >
                                ✔
                              </button>
                              <button
                                  type="button"
                                  className="inline-btn inline-btn-cancel"
                                  onClick={cancelInlineEdit}
                              >
                                ✖
                              </button>
                            </div>
                        )}
                      </td>

                      {/* SORT ORDER CELL with inline editor + pencil */}
                      <td
                          className="clickable-cell"
                          onClick={(e) => {
                            e.stopPropagation();
                            startInlineEdit(category, 'sort_order');
                          }}
                      >
                    <span>
                      {category.sort_order ?? '—'}
                      <span
                          className="cat-edit-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            startInlineEdit(category, 'sort_order');
                          }}
                      >
                        ✏️
                      </span>
                    </span>

                        {isSortEditing && (
                            <div
                                className="inline-editor"
                                onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                  type="number"
                                  value={inlineEdit.value}
                                  onChange={(e) =>
                                      setInlineEdit({
                                        ...inlineEdit,
                                        value: e.target.value,
                                      })
                                  }
                              />
                              <button
                                  type="button"
                                  className="inline-btn inline-btn-save"
                                  onClick={handleInlineSave}
                              >
                                ✔
                              </button>
                              <button
                                  type="button"
                                  className="inline-btn inline-btn-cancel"
                                  onClick={cancelInlineEdit}
                              >
                                ✖
                              </button>
                            </div>
                        )}
                      </td>

                      {/* ENABLED BADGE (click to toggle) */}
                      <td>
                    <span
                        className={
                          enabled ? 'badge badge-enabled' : 'badge badge-disabled'
                        }
                        onClick={() => handleToggleEnabledSingle(category)}
                        style={{ cursor: 'pointer' }}
                    >
                      {enabled ? 'Enabled' : 'Disabled'}
                    </span>
                      </td>

                      <td className="date-col">{formatDate(category.created_at)}</td>
                      <td className="date-col">{formatDate(category.updated_at)}</td>

                      <td>
                        <button
                            className="button-style-edit"
                            onClick={() => handleEditButtonClick(category, null)}
                        >
                          Edit
                        </button>
                        {!category.is_protected && (
                            <button
                                className="button-style-edit button-style-delete"
                                onClick={() => handleDeleteCategory(category.id)}
                            >
                              Del
                            </button>
                        )}
                      </td>
                    </tr>
                  </React.Fragment>
              );
            })}

            {filteredVisibleCategories.length === 0 && (
                <tr>
                  <td colSpan={11} style={{ textAlign: 'center', padding: '16px' }}>
                    No categories found.
                  </td>
                </tr>
            )}
            </tbody>
          </table>
        </div>
      </div>
  );
};

export default Categories;